import { ToggleButtons, Button } from "@/components";
import { CustomSelect, TextInput } from "@/components";
import { GeneralPlayer } from "@/components";
import { useFormik } from "formik";
import { useState, useEffect, useCallback, useMemo } from "react";
import * as Yup from "yup";
import { toast } from "@/components";
import { connect, ConnectedProps } from "react-redux";
import { MediaMetaApi } from "@/database";
import { getVideoIdFromUrl, extractUrlFromEmbed } from "./utils";
import { RootState } from "../../store";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useEmbedUrlValidation } from "./hooks/useEmbedUrlValidation";
import { useEmbedMediaOperations } from "./hooks/useEmbedMediaOperations";
import Modal from "@/components/Modal/Modal";
import MediaPublish from "../../routes/pages/MediaPublish/MediaPublish";
import {
  getMedia,
  updateIsClearFilter,
  updateIsFilter,
} from "@/store/media/media.action";
import { getUpdateMedia } from "@/store/media/media.action";
import { callRegisterCustomMediaOnCms } from "@/store/upload/upload.action";
import { setMediaDetails } from "@/store/media/media.slice";

interface IEmbedForm {
  closeModal: () => void;
  openUpload?: () => void;
  currentMediaDetailsForEdit?: any;
  getMedia: any;
  fromDetails?: boolean;
  onEmbedUpdated?: (id: any) => void;
  autoValidateOnMount?: boolean;
  setModalBtnDisabled?: (disabled: boolean) => void;
  onAssetTypeChange?: (assetType: string) => void;
}

interface EmbedForm {
  Embed_Url: string;
  AssetType: string;
  Streaming: string;
  EmbedType: string;
}
const EmbedForm = ({
  closeModal,
  openUpload,
  callRegisterCustomMediaOnCms,
  setMediaDetails,
  getMedia,
  updateIsClearFilter,
  updateIsFilter,
  currentMediaDetailsForEdit,
  fromDetails = false,
  getUpdateMedia,
  onEmbedUpdated,
  setModalBtnDisabled,
  onAssetTypeChange,
  userSocialChannels,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [isMediaDetailsModalOpen, setIsMediaDetailsModalOpen] = useState(false);
  const [publishModal, setPublishModal] = useState(false);
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [isChangingTab, setIsChangingTab] = useState(false);

  const { navigateWithScroll } = useScrollToTop();

  const EMBED_OPTIONS = useMemo(
    () => [
      { label: "Youtube", value: "0" },
      // { label: "Facebook", value: "1" },
      { label: "Stream URL", value: "2" },
    ],
    []
  );

  const playerTypeMap = useMemo<Record<string, string>>(
    () => ({
      "0": "yt",
      "1": "fb",
      "2": "hls",
    }),
    []
  );

  const mediaMetaApiPlatformMap = useMemo<Record<string, string>>(
    () => ({
      "0": "youtube",
      "1": "facebook",
      "2": "hls",
    }),
    []
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        Embed_Url: Yup.string()
          .test(
            "is-url-or-id",
            "Embed URL Id should be either URL or Id",
            function (value) {
              if (!value) return false;
              const extracted = extractUrlFromEmbed(value);
              const urlRegex = new RegExp("^(http|https)://");
              const idRegex = new RegExp("^[a-zA-Z0-9-_]{1,50}$");
              return urlRegex.test(extracted) || idRegex.test(extracted);
            }
          )
          .required("Embed URL or ID is required"),
      }),
    []
  );

  const handleTabClick = () => {
    if (isValidatingUrl) return;
    setIsChangingTab(true);
  };

  // Reset isChangingTab when contentType changes
  useEffect(() => {
    if (isChangingTab) {
      setIsChangingTab(false);
    }
  }, [isChangingTab]);

  const handleMediaDetailsOpen = useCallback(() => {
    if (openUpload) {
      openUpload();
    } else {
      setIsMediaDetailsModalOpen(true);
      setTimeout(() => {
        closeModal();
      }, 50);
    }
  }, [openUpload, closeModal]);

  const formik = useFormik<EmbedForm>({
    initialValues: {
      Embed_Url: currentMediaDetailsForEdit?.embed?.id || "",
      AssetType: currentMediaDetailsForEdit?.asset_type || "video",
      Streaming: currentMediaDetailsForEdit?.islive === 1 ? "Live" : "Non Live",
      EmbedType:
        currentMediaDetailsForEdit?.embed?.type === "facebook"
          ? "1"
          : currentMediaDetailsForEdit?.embed?.type === "hls"
            ? "2"
            : "0",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // This will be set after hooks are initialized
    },
  });

  const handleMediaMetaSave = useCallback(
    (mediaId: string) => {
      setLoading(false);
      setMediaId(mediaId);
      handleMediaDetailsOpen();
    },
    [handleMediaDetailsOpen]
  );

  // Initialize media operations hook
  const { handleMediaUpdate, handleMediaCreation, handleFormSubmit } =
    useEmbedMediaOperations({
      formik,
      fromDetails,
      currentMediaDetailsForEdit,
      getUpdateMedia,
      callRegisterCustomMediaOnCms: callRegisterCustomMediaOnCms as (
        data: any,
        callback?: (resp: any) => void
      ) => any,
      setMediaDetails,
      updateIsClearFilter,
      updateIsFilter,
      onEmbedUpdated,
      closeModal,
      onMediaMetaSave: handleMediaMetaSave,
      setLoading,
      toast,
    });

  // Initialize URL validation hook
  const {
    mediaMetadata,
    showPlayer,
    isUrlLocked,
    isValidatingUrl,
    disableLive,
    validateUrl,
    handleClearUrl,
    resetValidationState,
    setShowPlayer,
    setDisableLive,
  } = useEmbedUrlValidation({
    formik,
    embedType: formik.values.EmbedType,
    userSocialChannels,
    mediaMetaApiPlatformMap,
    onMediaUpdate: handleMediaUpdate,
    onMediaCreation: handleMediaCreation,
    fromDetails,
    MediaMetaApi,
    toast,
  });

  const handleSave = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      Object.keys(formik.initialValues).forEach((field) => {
        formik.setFieldTouched(
          field as keyof typeof formik.initialValues,
          true
        );
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }
      if (!mediaMetadata) {
        await validateUrl(false);
        return;
      }
      // Use handleFormSubmit directly instead of formik.handleSubmit
      if (handleFormSubmit) {
        handleFormSubmit(mediaMetadata);
      }
    },
    [formik, mediaMetadata, validateUrl, handleFormSubmit]
  );

  useEffect(() => {
    const handelNextClick = () => {
      handleSave();
    };
    window.addEventListener("nextClickEmbed", handelNextClick);
    return () => {
      window.removeEventListener("nextClickEmbed", handelNextClick);
    };
  }, [formik.values.Embed_Url, mediaMetadata, handleSave]);

  const handleCloseModal = () => {
    setIsMediaDetailsModalOpen(false);
    closeModal();
  };

  const handleBackClick = () => {
    getMedia({ id: currentMediaDetailsForEdit.id });
    handleMediaDetailsOpen();
    setPublishModal(false);
  };

  const handleDone = () => {
    updateIsClearFilter && updateIsClearFilter(true);
    updateIsFilter && updateIsFilter(false);
    const event = new CustomEvent("doneClick");
    window.dispatchEvent(event);
    navigateWithScroll(`/media/${currentMediaDetailsForEdit?.asset_type}`);
  };

  useEffect(() => {
    if (formik.values.Embed_Url.length === 0 || loading) {
      setModalBtnDisabled && setModalBtnDisabled(true);
    } else {
      setModalBtnDisabled && setModalBtnDisabled(false);
    }
  }, [formik.values.Embed_Url]);

  return (
    <div style={{ width: " calc(100% + 32px)", marginLeft: "-16px" }}>
      <div className="d-flex justify-content-center mb-4">
        <ToggleButtons
          options={EMBED_OPTIONS}
          selectedValue={formik.values.EmbedType}
          onTabClick={handleTabClick}
          handleChange={(val: string) => {
            resetValidationState();
            formik.setFieldValue("EmbedType", val);
            formik.setFieldTouched("Embed_Url", false, false);
            formik.setFieldValue("Embed_Url", "");
          }}
          name="embedType"
          disabled={isValidatingUrl}
          label=""
        />
      </div>
      <div className="border-bottom px-4 position-relative">
        <div className="d-flex gap-2 mb-4 border">
          <div className="d-flex flex-grow-1 justify-content-between">
            <div className="d-flex flex-grow-1 position-relative border-end p-3 gap-2 flex-column">
              <div
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    validateUrl(true);
                  }
                }}
              >
                <div className="d-flex align-items-center position-relative">
                  <TextInput
                    width="w-100"
                    name={"Embed_Url"}
                    placeholder="Embed URL or ID"
                    value={formik.values.Embed_Url}
                    onChange={(e) => {
                      formik.handleChange(e);
                      setDisableLive(false);
                      formik.setFieldTouched("Embed_Url", false, false);
                      formik.setFieldError("Embed_Url", "");
                      if (mediaMetadata) {
                        resetValidationState();
                      }
                    }}
                    error={formik.errors.Embed_Url}
                    touched={formik.touched.Embed_Url}
                    label="Embed Info"
                    showRequired={false}
                    classNameLabel="position-absolute px-1 bg-white fs-6 top-25"
                    onBlur={() => {
                      if (isChangingTab) return;
                      formik.handleBlur("Embed_Url");
                      formik.setFieldTouched("Embed_Url", true, false);
                      validateUrl(true);
                    }}
                    disabled={isUrlLocked}
                    endIcon={
                      isValidatingUrl ? (
                        <i className="spinner-border spinner-border-sm text-primary" />
                      ) : isUrlLocked ? (
                        <i
                          className="bi bi-x iconSvg"
                          style={{ cursor: "pointer" }}
                          title="Clear"
                          onClick={() => {
                            handleClearUrl();
                            setDisableLive(false);
                          }}
                        />
                      ) : null
                    }
                  />
                </div>
              </div>
              <div className="rounded ratio ratio-16x9 ">
                {showPlayer ? (
                  <>
                    <GeneralPlayer
                      id="embed"
                      embedUpdateKey={mediaMetadata?.id}
                      remoteConfig={true}
                      playerType={playerTypeMap[formik.values.EmbedType]}
                      videoData={
                        formik.values.EmbedType === "2"
                          ? { url: formik.values.Embed_Url }
                          : {
                              mid: getVideoIdFromUrl(
                                playerTypeMap[formik.values.EmbedType],
                                formik.values.Embed_Url
                              ),
                            }
                      }
                      videoUrl={
                        formik.values.EmbedType === "2"
                          ? formik.values.Embed_Url
                          : undefined
                      }
                    />
                    {loading && (
                      <div
                        className="d-flex align-items-center justify-content-center h-100 w-100 position-absolute top-50 start-50 translate-middle"
                        id="loader"
                      >
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 w-100 bgGrey rounded">
                    <span>Preview</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className="d-flex p-2 flex-column mb-5 gap-2"
            style={{ flex: "0 0 250px" }}
          >
            <div>
              <CustomSelect
                value={formik.values.Streaming}
                label="Live/ On demand"
                placeholder="live"
                disabled={disableLive}
                options={[
                  { label: "Live", value: "Live" },
                  { label: "Non Live", value: "Non Live" },
                ]}
                handleChange={(newValue: any) =>
                  formik.setFieldValue("Streaming", newValue.value)
                }
              />
            </div>
            <div>
              <CustomSelect
                value={formik.values.AssetType}
                label="Asset Type"
                placeholder="Video"
                options={[
                  { label: "Audio", value: "audio" },
                  { label: "Video", value: "video" },
                ]}
                handleChange={(newValue: any) => {
                  formik.setFieldValue("AssetType", newValue.value);
                  onAssetTypeChange?.(newValue.value);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* <div className="d-flex justify-content-end mt-3 px-4">
        <Button type="primary" isDisabled={formik.values.Embed_Url.length === 0 || loading} onClick={handleSave}>
          {fromDetails ? 'UPDATE' : 'NEXT'}
        </Button>
      </div> */}
      {publishModal && (
        <Modal
          isOpen={publishModal}
          title="Social Publish"
          onClose={() => {
            setPublishModal(false);
            updateIsClearFilter && updateIsClearFilter(true);
            updateIsFilter && updateIsFilter(false);
            handleCloseModal();
          }}
          height="100vh"
          width="1060px"
          closeOnOutsideClick={false}
          footer={
            <div className="d-flex justify-content-between w-100">
              <Button type="outline-primary" onClick={handleBackClick}>
                Back
              </Button>
              <Button onClick={handleDone}>Save</Button>
            </div>
          }
        >
          <MediaPublish
            isWizard={true}
            closeHandler={() => {
              setPublishModal(false);
              handleCloseModal();
            }}
          />
        </Modal>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  userSocialChannels: state.app.userSocialChannels.socialChannelsList,
});
const mapDispatch = {
  callRegisterCustomMediaOnCms,
  setMediaDetails,
  getMedia,
  updateIsClearFilter,
  updateIsFilter,
  getUpdateMedia,
};

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & IEmbedForm;
export default connector(EmbedForm);
